import React, { useState, useEffect, useRef } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { Autocomplete, TextField, createFilterOptions } from "@mui/material";
import ClipLoader from "react-spinners/ClipLoader";
import axios from "axios";
import { renderPriceWithCurrencyWithoutUnit } from "@utils/utils";
import { useDebounce } from "use-debounce";
import {
  ContainerLabel
} from "../../AddEditInvoice.styled";
import {
  autoSearchInputStylesError,
  autoSearchInputStyles,
} from "@utils/extra-style";


const AttachedInvoice = (props) => {
  const { t, formData, setFormData, isEditMode, countryId } = props;

  const latestRequestRef = useRef(0);
  const [query, setQuery] = useState();
  const [debounceQuery] = useDebounce(query, 700);
  const [selectedCompany, setSelectedCompany] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [isCompanySuggestionsLoading, setCompanySuggestionsLoading] =
    useState(false);

  useEffect(() => {
    setFormData({
      ...formData,
      attachedInvoice: {
        value: selectedCompany,
        error: "",
        required: true,
      },
    });
  }, [selectedCompany]);

  useEffect(() => {
      if (isEditMode) {
        let entityGroupList = [];
        if (formData?.attachedInvoice?.value) {
          entityGroupList.push({
            name:formData?.attachedInvoice?.value?.name,
            id: formData?.attachedInvoice?.value?.id,
            price: formData?.attachedInvoice?.value?.price,
          });
        }
        setSelectedCompany(entityGroupList[0]);
      } else if (!isEditMode) {
        const url = `/general/search?search=&limit=50&countries=[${countryId}]&objectTypes=["Invoice"]`;
        getEntityData(url, "selectedDefault");
      }
    }, []);
  
    const searchEntity = (e) => {
      const search = query;
      if (search) {
        let url = `/general/search?search=${search}&limit=50&countries=[${countryId}]&objectTypes=["Invoice"]`;
        getEntityData(url, "all");
      }
    };

     const handleChange = (e) => {
       setQuery(e.target.value || undefined);
     };

    useEffect(() => {
        searchEntity();
      }, [debounceQuery]);
 
    const getEntityData = (url, type) => {
      const requestId = ++latestRequestRef.current;

      setCompanySuggestionsLoading(true);

      axios
        .get(url)
        .then((response) => {
          // Ignore responses from older requests
          if (requestId !== latestRequestRef.current) {
            return;
          }

          const invoices = response?.data?.invoices || [];

          const entityGroupList = invoices.map((key) => ({
            price: renderPriceWithCurrencyWithoutUnit(
              Number(key.totalExcludedTaxes ?? 0).toFixed(2),
              key.currency.symbol,
              key.currency.position
            ),
            name: key.documentNumber,
            id: key.id,
          }));

          setCompanyList(entityGroupList);

          if (type === "selectedDefault" && invoices.length === 1) {
            setSelectedCompany(entityGroupList[0]);
          }
        })
        .catch((error) => {
          if (requestId !== latestRequestRef.current) {
            return;
          }

          if (window.debugMode) {
            console.error("Error fetching invoices", error);
          }

          setCompanyList([]);
        })
        .finally(() => {
          if (requestId === latestRequestRef.current) {
            setCompanySuggestionsLoading(false);
          }
        });
    };
    
  return (
    <>
      <ContainerLabel error={formData.attachedInvoice.error}>
        {" "}
        {t("CLIENT_INVOICING_ATTACHED_INVOICE")}
        {isCompanySuggestionsLoading && (
          <ClipLoader size={15} background="#15aee5 !important" />
        )}
      </ContainerLabel>
      <Autocomplete
        id="tags-outlined"
        // style={{ zIndex: 99 }}
        value={selectedCompany}
        onChange={(event, newValue) => {
          if (typeof newValue === "string") {
            setSelectedCompany({
              name: newValue,
            });
          } else if (newValue && newValue.inputValue) {
            // Create a new value from the user input
            setSelectedCompany({
              name: newValue.inputValue,
            });
          } else {
            setSelectedCompany(newValue);
          }
        }}
        filterOptions={(options) => options}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        options={companyList}
        getOptionLabel={(option) => {
          // Value selected with enter, right from the input
          if (typeof option === "string") {
            return option || "";
          }
          // Add "xxx" option created dynamically
          if (option.inputValue) {
            return option.inputValue || "";
          }
          // Regular option
          return option.name || "";
        }}
        noOptionsText={t("GENERIC_NO_RESULTS")}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            <Box
              sx={{
                display: "flex",

                justifyContent: "space-between",

                alignItems: "center",

                width: "100%",
              }}
            >
              <Box sx={{ textAlign: "left" }}>{option.name}</Box>

              <Box sx={{ textAlign: "right" }}>{option.price}</Box>
            </Box>
          </Box>
        )}
        onKeyUp={handleChange}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        // sx={{ width: 300 }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder=""
            size="small"
            style={
              formData.attachedInvoice.error !== ""
                ? autoSearchInputStylesError
                : autoSearchInputStyles
            }
          />
        )}
      />
    </>
  );
};

AttachedInvoice.propTypes = {
  t: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
};

AttachedInvoice.defaultProps = {
  isEditMode: false,
};
export default withTranslation()(AttachedInvoice);
